/* eslint-disable no-underscore-dangle */
import Yup from '../../i18n/yup';
import { v1 as uuidv1 } from 'uuid';

import { getDateAndTime } from '../../utils/dateFormat';

const nameRegex = new RegExp('^[a-zA-Z0-9\\ ñÑ\\-+#_.,!¡¿?&ÁÉÍÓÚáéíóú]*$')
const dateRegex = /\d\d\d\d-\d\d-\d\d/
const timeRegex = /\d\d:\d\d/
// const youtubeUrlRegex = new RegExp('^[A-Za-z0-9_-]*$')

export const baseUrl = `${process.env.REACT_APP_API_URL}/campaign`;

export const getRequestToFormData = (QUESTIONS_REVERSE_DICTIONARY) => (data) => {
  const { assets, dates, filters, formData } = data.splits[0]
  const starts = getDateAndTime(dates.begin);
  const ends = getDateAndTime(dates.end);

  let type;
  let questions = [];

  if (formData && formData.length > 0) {
    type = 'Form';
    questions = formData.map(q => ({
      id: q._id,
      name: q.value,
      type: QUESTIONS_REVERSE_DICTIONARY[q.type],
      options: q.options.map(opt => ({
        id: uuidv1(),
        name: opt,
      })),
    }));
  } else {
    type = assets.video.length === 11 ? 'Video' : 'Image';
  }

  const match = /^images\/(.*)/.exec(assets.logoApp)

  return {
    name: data.name,
    video: assets.video,
    redirect: assets.redirectUrl,
    placement: filters[0].placements[0],
    gender: data.targeting.demographic.gender,
    type,
    thumbnail: {
      thumbnailCampaign: match ? match[1] : null,
    },
    starts,
    ends,
    minAge: data.targeting.demographic.age.min,
    maxAge: data.targeting.demographic.age.max,
    questions,
  };
};

export const getFormToRequestData = (QUESTIONS_DICTIONARY) => (values, ignoreFormQuestion = false) => {
  const data = {
    name: values.name,
    splits: [
      {
        assets: {
          redirectUrl: values.redirect,
          logoApp: values.thumbnail.thumbnailCampaign,
        },
        subtype: values.type === 'Form' ? 'Form' : 'Video',
        dates: {
          begin: `${values.starts.date} ${values.starts.time}:00`,
          end: `${values.ends.date} ${values.ends.time}:00`,
        },
        filters: [
          {
            placements: [values.placement]
          },
        ],
      },
    ],
    targeting: {
      demographic: {
        age: {
          min: values.minAge,
          max: values.maxAge
        },
        gender: values.gender,
      },
    },
  };

  if (values.type === 'Form' && !ignoreFormQuestion) {
    data.formData = values.questions.map((q) => ({
      value: q.name,
      type: QUESTIONS_DICTIONARY[q.type],
      options: q.options && q.options.map((opt) => opt.name),
    }));
  } else {
    data.splits[0].assets.video = values.video;
  }

  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  };
};

export const initialValues = {
  name: '',
  redirect: '',
  placement: null,
  gender: null,
  type: 'Form',
  thumbnail: {
    thumbnailCampaign: '',
  },
  starts: {
    date: '',
    time: '',
  },
  ends: {
    date: '',
    time: '',
  },
  minAge: '',
  maxAge: '',

  video: '',
  image: '',
  questions: [],
};

export const validationSchema = Yup.object().shape({
  name: Yup.string().required().min(3).max(100).matches(nameRegex, 'CAMPAIGN.FORM_VALIDATION.NAME_FEEDBACK'),
  redirect: Yup.string().url().required(),
  placement: Yup.string().required().nullable(),
  gender: Yup.string().required().nullable(),
  type: Yup.string().required(),
  thumbnail: Yup.object().shape({
    thumbnailCampaign: Yup.string().required(),
  }).nullable().required(),
  starts: Yup.object().shape({
    date: Yup.string().matches(dateRegex).required(),
    time: Yup.string().matches(timeRegex).required(),
  }),
  ends: Yup.object().shape({
    date: Yup.string().matches(dateRegex).required(),
    time: Yup.string().matches(timeRegex).required(),
  }).test('ends', { time: 'CAMPAIGN.FORM_VALIDATION.END_DATE' }, function (data) {
    const startDate = `${this.parent.starts.date} ${this.parent.starts.time}`;
    const endDate = `${data.date} ${data.time}`;
    return endDate > startDate;
  }),
  minAge: Yup.number().min(1).max(98)
    .typeError('COMMON.NUMBERS.MUST_BE_NUMBER')
    .required(),
  maxAge: Yup.number().min(1).max(99)
    .typeError('COMMON.NUMBERS.MUST_BE_NUMBER')
    .moreThan(Yup.ref('minAge'), 'CAMPAIGN.FORM_VALIDATION.MAX_AGE_FEEDBACK_2')
    .required(),
  video: Yup.string().when('type', {
    is: 'Video',
    then: Yup.string().min(11).max(32).length(11, 'CAMPAIGN.FORM_VALIDATION.VIDEO_FEEDBACK').required(),
  }).when('type', {
    is: 'Image',
    then: Yup.string().length(32, 'CAMPAIGN.FORM_VALIDATION.VIDEO_FEEDBACK').required(),
  }),
  questions: Yup.array().when('type', {
    is: 'Form',
    then: Yup.array().of(Yup.object({
      name: Yup.string().min(3).required(),
    })).min(1, 'CAMPAIGN.FORM_VALIDATION.QUESTIONS_FEEDBACK'),
  }),
})

export const formsInputsData = [
  {
    id: 'name',
    title: 'CAMPAIGN.FORMS_INPUTS.NAME',
    type: 'text',
    iconName: 'name',
  },
  {
    id: 'redirect',
    title: 'CAMPAIGN.FORMS_INPUTS.REDIRECT',
    type: 'url',
    iconName: 'url',
  },
  {
    id: 'placement',
    title: 'CAMPAIGN.FORMS_INPUTS.PLACEMENT',
    type: 'select',
    options: [],
    url: `${process.env.REACT_APP_API_URL}/placement/`,
    transform: options => options.map(opt => ({
      value: opt._id,
      label: opt.name,
    })),
    iconName: 'placement',
  },
  {
    id: 'gender',
    title: 'CAMPAIGN.FORMS_INPUTS.GENDER',
    type: 'select',
    options: [
      { value: 'Male', label: 'CAMPAIGN.MALE' },
      { value: 'Female', label: 'CAMPAIGN.FEMALE' },
      { value: 'Both', label: 'CAMPAIGN.BOTH' },
    ],
    iconName: 'gender',
  },
  {
    id: 'thumbnail',
    title: 'CAMPAIGN.THUMBNAIL.TITLE',
    text: 'CAMPAIGN.THUMBNAIL.TEXT',
    type: 'image',
    uploadUrl: `${baseUrl}/upload-thumbnail`,
    getImagePath: (value) => value?.thumbnailCampaign ? `images/${value.thumbnailCampaign}` : null,
  },
  {
    id: 'starts',
    dateTitle: 'CAMPAIGN.FORMS_INPUTS.START_DATE',
    timeTitle: 'CAMPAIGN.FORMS_INPUTS.START_TIME',
    type: 'dateandtime',
    iconName: 'name',
  },
  {
    id: 'ends',
    dateTitle: 'CAMPAIGN.FORMS_INPUTS.END_DATE',
    timeTitle: 'CAMPAIGN.FORMS_INPUTS.END_TIME',
    type: 'dateandtime',
    iconName: 'name',
  },
  {
    id: 'minAge',
    title: 'CAMPAIGN.FORMS_INPUTS.MIN_AGE',
    type: 'text',
    iconName: 'minimum',
  },
  {
    id: 'maxAge',
    title: 'CAMPAIGN.FORMS_INPUTS.MAX_AGE',
    type: 'text',
    iconName: 'maximum',
  },
];

export const typeInputProps = [{
  id: 'type',
  title: 'CAMPAIGN.TYPE_INPUT.TYPE',
  type: 'select',
  options: [
    { value: 'Video', label: 'CAMPAIGN.TYPE_INPUT.VIDEO' },
    { value: 'Image', label: 'CAMPAIGN.TYPE_INPUT.IMAGE' },
    { value: 'Form', label: 'CAMPAIGN.TYPE_INPUT.FORM' },
  ],
  iconName: 'type',
}];
